import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { RoleProvider } from './context/RoleContext';
import { useRole } from './context/RoleContext';
import { useLanguage } from './context/LanguageContext';
import { getUserGeoLocation } from './services/GeoLocationService';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import About from './pages/About';
import MyHub from './pages/MyHub';
import Partners from './pages/Partners';
import DashboardAdmin from './pages/DashboardAdmin';
import DashboardClient from './pages/DashboardClient';
import SettingClient from './pages/SettingClient';
import SupportClient from './pages/SupportClient';
import BillingClient from './pages/BillingClient';
import ControlAccessAdmin from './pages/ControlAccessAdmin';
import AgentSupport from './pages/AgentSupport';
import CustomerServicesManager from './pages/CustomerServicesManager';
import MyhubAdmin from './pages/MyhubAdmin';
import CostSimulator from './pages/CostSimulator';
// We will create these pages later
// import TicketsList from './pages/TicketsList';
// import TicketDetail from './pages/TicketDetail';
// import NewTicket from './pages/NewTicket';
// import NotFound from './pages/NotFound';

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#015480', // Company primary blue
      dark: '#013e5f',
      light: '#0277bd',
    },
    secondary: {
      main: '#03a9f4', // Complementary light blue
      dark: '#0288d1',
    },
    success: {
      main: '#00897b',
    },
    warning: {
      main: '#ff9800',
    },
    text: {
      primary: '#01395a',
      secondary: '#546e7a',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Outfit", "Plus Jakarta Sans", "Inter", sans-serif',
    h1: {
      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.025em',
      background: 'linear-gradient(90deg, #015480, #0288d1)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h2: {
      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.025em',
      color: '#015480',
    },
    h3: {
      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
      fontWeight: 700,
      color: '#015480',
    },
    h4: {
      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
      fontWeight: 600,
      color: '#015480',
    },
    h5: {
      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
      fontWeight: 600,
      color: '#015480',
    },
    h6: {
      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
      fontWeight: 600,
      color: '#015480',
    },
    body1: {
      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
      lineHeight: 1.6,
    },
    subtitle1: {
      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
      lineHeight: 1.6,
    },
    subtitle2: {
      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          boxShadow: 'none',
          fontWeight: 600,
          padding: '0.75rem 1.5rem',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 6px -1px rgba(1, 84, 128, 0.2)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #015480, #0288d1)',
          '&:hover': {
            background: 'linear-gradient(135deg, #013e5f, #0277bd)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          color: '#015480',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 25px -5px rgba(1, 84, 128, 0.1)',
            borderColor: '#cbd5e1',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused fieldset': {
              borderColor: '#015480',
            }
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [
          {
            fontFamily: 'Outfit',
            fontStyle: 'normal',
            fontWeight: 400,
            fontDisplay: 'swap',
            src: `url(https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap)`,
          },
          {
            fontFamily: 'Plus Jakarta Sans',
            fontStyle: 'normal',
            fontWeight: 400,
            fontDisplay: 'swap',
            src: `url(https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap)`,
          },
        ],
        body: {
          backgroundColor: '#f8fafc',
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(1, 84, 128, 0.03) 0%, transparent 25%),
            radial-gradient(circle at 75% 75%, rgba(3, 169, 244, 0.03) 0%, transparent 25%)
          `,
          backgroundAttachment: 'fixed',
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Admin route component - only accessible to users with roleId 1
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useRole();

  console.log('AdminRoute - authLoading:', authLoading, 'roleLoading:', roleLoading);
  console.log('AdminRoute - currentUser:', currentUser);
  console.log('AdminRoute - isAdmin:', isAdmin);

  if (authLoading || roleLoading) {
    console.log('AdminRoute - Loading...');
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    console.log('AdminRoute - No current user, redirecting to login');
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    console.log('AdminRoute - Not admin, redirecting to client dashboard');
    return <Navigate to="/dashboard/client" />;
  }

  console.log('AdminRoute - User is admin, rendering children');
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { loading: languageLoading } = useLanguage();
  
  // Préchargement des informations de géolocalisation
  useEffect(() => {
    const initGeoLocation = async () => {
      try {
        // Cette fonction vérifie d'abord le cookie, puis fait une requête API si nécessaire
        await getUserGeoLocation();
      } catch (error) {
        console.error('Error initializing geolocation:', error);
      }
    };

    initGeoLocation();
  }, []);

  if (languageLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Box sx={{ flexGrow: 1 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/about" element={<About />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/cost-simulator" element={<CostSimulator />} />

          {/* Protected routes for all authenticated users */}
          <Route path="/myhub" element={<ProtectedRoute><MyHub /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingClient /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportClient /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><BillingClient /></ProtectedRoute>} />

          {/* Admin routes */}
          <Route path="/dashboard/admin" element={<AdminRoute><DashboardAdmin /></AdminRoute>} />
          <Route path="/admin/access-control" element={<AdminRoute><ControlAccessAdmin /></AdminRoute>} />
          <Route path="/admin/support" element={<AdminRoute><AgentSupport /></AdminRoute>} />
          <Route path="/admin/applications" element={<AdminRoute><MyhubAdmin /></AdminRoute>} />
          <Route path="/admin/myhub" element={<AdminRoute><MyhubAdmin /></AdminRoute>} />

          {/* Client routes */}
          <Route path="/dashboard/client" element={<ProtectedRoute><DashboardClient /></ProtectedRoute>} />

          {/* Redirect to home for any other route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Box>
      <Footer />
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <RoleProvider>
            <LanguageProvider>
              <AppContent />
            </LanguageProvider>
          </RoleProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
